import { useRef, useEffect, useState } from "react";
import { 
  Archive, 
  FolderInput, 
  PlusCircle, 
  Reply, 
  PencilOff, 
  ArrowRight,
  List,
  Settings
} from "lucide-react";
import PrinceGeoffrey from "../Chat/PrinceGeoffrey";

const featureList = [
  {
    icon: <Archive className="text-stone-600" />,
    title: "Smart Archival",
    desc: "Instantly sweeps low-value emails out of sight.",
  },
  {
    icon: <ArrowRight className="text-blue-500" />,
    title: "Intuitive Organization",
    desc: 'Auto-labels “For-Review” and “Financial.”',
  },
  {
    icon: <PlusCircle className="text-emerald-600" />,
    title: "Task Auto-Generation",
    desc: "Turns hidden to-dos into actionable tasks.",
  },
  {
    icon: <Reply className="text-cyan-500" />,
    title: "Automated Replies",
    desc: "Drafts context-aware replies for your quick review.",
  },
  {
    icon: <FolderInput className="text-indigo-500" />,
    title: "Self-Learning Projects",
    desc: "Files insights to Projects so they stay up-to-date.",
  },
];

const comparisonRows = [
  {
    label: "<b>Smart Archival</b>",
    readOnly: "Suggests what to archive. You click to apply.",
    full: "Archives low-value mail automatically (with undo).",
  },
  {
    label: "<b>Intuitive Organization</b>",
    readOnly: 'Suggests “For-Review”/“Financial” labels. You click to apply.',
    full: "Labels applied automatically (with undo).",
  },
  {
    label: "Task Auto-Generation",
    readOnly: "Auto-creates tasks.",
    full: "Auto-creates tasks.",
  },
  {
    label: "Automated Replies",
    readOnly: "Drafts responses for your review; you send.",
    full: "Drafts responses for your review; you send",
  },
  {
    label: "Self-Learning Projects",
    readOnly: "Adds project context automatically.",
    full: "Adds project context automatically.",
  },
  {
    label: "Time saved",
    readOnly: "Less",
    full: "<b>More</b>",
  },
];

const safeguards = [
  "Activity Log of every action",
  "One-click Undo for every action",
  "No auto-send without your review",
];

const EmailTriageOnboardingModal = ({
  isOpen,
  onClose,
  onToggleReadOnlyMode,
  readOnlyMode
}) => {
  const modalRef = useRef(null);
  const [step, setStep] = useState(1);

  // Reset to step 1 when opened
  useEffect(() => {
   if (isOpen) setStep(1);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-[1000]">
      <div
        ref={modalRef}
        className="bg-white p-6 md:p-8 rounded-lg w-[95%] max-w-[600px] shadow-md relative"
      >
        {/* Step 1: How Email Triage Works */}
        {step === 1 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Meet Email Triage</h2>
            <div className="flex items-center gap-2 mb-2">
              <PrinceGeoffrey size={60} />
              <p className="text-gray-600 text-base mb-6 mt-1 w-full">
                Prince Geoffrey reviews new mail and applies smart actions to get you to Inbox Zero—fast and safely.
              </p>
            </div>
            <div className="flex flex-col gap-4 mb-6">
              {featureList.map((f, i) => (
                <div key={f.title} className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">{f.icon}</div>
                  <div>
                    <div className="font-semibold text-gray-800">{f.title}</div>
                    <div className="text-gray-600 text-sm">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-gray-500 mb-6">
              <span className="text-sm font-semibold">Note:</span> He never sends an email without you reviewing it.
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                className="bg-sky-500 hover:bg-sky-600 text-white font-bold px-6 py-2 rounded-md transition-colors"
                onClick={() => setStep(2)}
                data-amp-track-section="Read Only Mode Modal"
                data-amp-track-action="Step1_Next_Click"
              >
                Next &rarr;
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Read-Only Is On (Right Now) */}
        {step === 2 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200">
                <PencilOff size={16} className="mr-1" /> Read-Only Mode: {readOnlyMode ? 'On' : 'Off'}
              </span>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Choose your control level</h2>
            <p className="text-gray-600 text-base mb-4">
              Read-Only is a safe way to try triage, but it adds extra steps. We recommend turning it off so Prince Geoffrey can archive and label for you automatically.
            </p>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border border-gray-200 rounded-lg bg-gray-50">
                <thead>
                  <tr>
                    <th className="w-1/4 py-2 px-2 bg-white"></th>
                    <th className="w-1/3 py-2 px-2 bg-sky-50 text-sky-700 font-semibold text-center border-l border-gray-200">
                      Read-Only<br/>({readOnlyMode ? 'current' : 'not recommended'})
                    </th>
                    <th className="w-1/3 py-2 px-2 bg-emerald-50 text-emerald-700 font-semibold text-center border-l border-gray-200">
                      Full Automation<br/>({readOnlyMode ? 'recommended' : 'current'})
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row, i) => (
                    <tr key={row.label} className="border-t border-gray-200">
                      <td className="py-2 px-2 font-medium text-gray-700">
                        <span dangerouslySetInnerHTML={{ __html: row.label }} />
                      </td>
                      <td className="py-2 px-2 border-l border-gray-200 text-gray-700">
                        <span dangerouslySetInnerHTML={{ __html: row.readOnly }} />
                      </td>
                      <td className="py-2 px-2 border-l border-gray-200 text-gray-700">
                        <span dangerouslySetInnerHTML={{ __html: row.full }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mb-6">
              <div className="font-semibold text-gray-800 mb-1">Safeguards (always on):</div>
              <ul className="list-disc pl-5 text-gray-600 text-sm space-y-1">
                {safeguards.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>
            <div className="flex justify-between gap-2">
              <button
                type="button"
                className="ml-0 px-5 py-2 rounded-md border border-gray-300 bg-gray-100 text-gray-800 font-semibold hover:bg-gray-200 transition-colors"
                onClick={() => setStep(1)}
                data-amp-track-section="Read Only Mode Modal"
                data-amp-track-action="Step2_Back_Click"
              >
                &larr; Back
              </button>
              <button
                type="button"
                className="ml-0 px-6 py-2 rounded-md bg-sky-500 text-white font-bold hover:bg-sky-600 transition-colors"
                onClick={() => setStep(3)}
                data-amp-track-section="Read Only Mode Modal"
                data-amp-track-action="Step2_Next_Click"
              >
                Next &rarr;
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Select Your Mode */}
        {step === 3 && (
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">How do you want to run triage?</h2>
            <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-6">
              <p className="text-gray-600 text-base">
                You can change this anytime in Digest Settings:
              </p>
              <div
                className="flex flex-row items-center justify-start text-md font-semibold gap-2 ml-0 px-3 py-1 border-solid border rounded-3xl border-slate-300 text-slate-600 bg-transparent"
              >
                <List size={16} />
                <Settings size={16} />
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="w-full md:w-1/2">
                <button
                  type="button"
                  className="w-full ml-0 mb-2 bg-gray-100 text-gray-800 rounded-md px-4 py-4 flex flex-col items-center font-bold hover:bg-gray-200 transition-colors"
                  onClick={() => {
                    onToggleReadOnlyMode(true);
                    onClose();
                  }}
                  data-amp-track-section="Read Only Mode Modal"
                  data-amp-track-action="Keep_Read_Only_Click"
                  data-amp-track-description="Keep Read Only Mode On"
                >
                  <span className="font-bold text-lg mb-1">
                    {readOnlyMode ? 'Keep Read-Only On' : 'Turn Read-Only On'}
                  </span>
                  <span className="text-xs font-normal">{readOnlyMode ? '(Current)' : '(Not Recommended)'}</span>
                  </button>
                <div className="font-normal text-sm text-center text-gray-500">You’ll apply archive and label suggestions manually.</div>
              </div>
              <div className="w-full md:w-1/2">
                <button
                  type="button"
                  className="w-full ml-0 mb-2 bg-sky-500 text-white rounded-md px-4 py-4 flex flex-col items-center font-bold hover:bg-sky-600 transition-colors"
                  onClick={() => {
                    onToggleReadOnlyMode(false);
                    onClose();
                  }}
                  data-amp-track-section="Read Only Mode Modal"
                  data-amp-track-action="Toggle_Read_Only_Click"
                  data-amp-track-description="Toggle Read Only Mode Off"
                >
                  <span className="font-bold text-lg mb-1">
                    {readOnlyMode ? 'Turn Read-Only Off' : 'Keep Read-Only Off'}
                  </span>
                  <span className="text-xs font-normal">{readOnlyMode ? '(Recommended)' : '(Current)'}</span>
                </button>
                <div className="font-normal text-sm text-center text-gray-500">Prince Geoffrey will archive and label automatically.</div>
              </div>
            </div>
            <div className="text-sm text-gray-500 mt-2 border-t border-gray-100 pt-4 ">
              <span className="font-semibold">Note:</span> Even with Read-Only on, Smart Archival and Intuitive Organization still run—but only as suggestions. You’ll need to take an extra step to archive or label.
            </div>
            <div className="flex justify-start mt-6">
              <button
                type="button"
                className="ml-0 px-5 py-2 rounded-md border border-gray-300 bg-gray-100 text-gray-800 font-semibold hover:bg-gray-200 transition-colors"
                onClick={() => setStep(2)}
                data-amp-track-section="Read Only Mode Modal"
                data-amp-track-action="Step3_Back_Click"
              >
                &larr; Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailTriageOnboardingModal;
